import React, { useState, useEffect } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { ConfirmModal } from '@apply/ui';
import { FileSelectDialog, InformModal } from '../../../common/SharedComponents';
import { FloodReportStatusType, FloodReportFileType } from '../LoanConstants';
import { formatPhone } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';

export const FloodReport = ({ floodReport, canOrder, onCancel, onChange }) => {
    const [openConfirmOverwriteModal, set_openConfirmOverwriteModal] = useState(false);
    const [openInformModal, set_openInformModal] = useState(false);
    const [informModalText, set_informModalText] = useState("Your request has been saved and will be placed upon 'Save'");

    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const [uploadDisabled, set_uploadDisabled] = useState(false);
    const [orderDisabled, set_orderDisabled] = useState(false);
    const [cancelDisabled, set_cancelDisabled] = useState(false);

    const styles = {
        formRow: { marginBottom: '0' },
        formColumn: { paddingBottom: '3px' },
        iconContainer: {
            paddingLeft: '0',
            paddingRight: '0',
            paddingBottom: '3px',
        },
        iconYes: { color: 'mediumblue', cursor: 'pointer' },
        iconNo: { color: 'silver', cursor: 'not-allowed' },
        iconCancel: { color: 'darkred', cursor: 'pointer' },
    };

    const getStatus = (statusId) => {
        switch (statusId) {
            case FloodReportStatusType.Cancelled:
                return 'Order Cancelled';
            case FloodReportStatusType.Complete:
                return 'File available for download';
            case FloodReportStatusType.NotFound:
                return 'Order Not Placed - Address not found in SvcLink database!';
            case FloodReportStatusType.Error:
                return 'Error occurred when placing order with SvcLink';
            case FloodReportStatusType.MissingInformation:
                return 'Order Placed - Additional information required by SvcLink';
            case FloodReportStatusType.PendingRequest:
                return 'Order Pending - Request not yet sent to SvcLink';
            case FloodReportStatusType.PendingUploadToBlobStorage:
                return 'Order received - Report not yet available for download';
            case FloodReportStatusType.RequiresManualResearch:
                return 'Order Placed - Awaiting report from SvcLink';
            case FloodReportStatusType.PendingCancel:
                return 'Cancel Placed - Awaiting confirmation from SvcLink';
            default:
                return '';
        }
    };

    const canUploadFile = (floodReport) => {
        switch (floodReport?.statusId) {
            case FloodReportStatusType.Cancelled:
                return true;
            case FloodReportStatusType.Complete:
                return true;
            case FloodReportStatusType.NotFound:
                return true;
            case FloodReportStatusType.Error:
                return true;
            case FloodReportStatusType.MissingInformation:
                return false;
            case FloodReportStatusType.PendingRequest:
                return false;
            case FloodReportStatusType.PendingUploadToBlobStorage:
                return false;
            case FloodReportStatusType.RequiresManualResearch:
                return false;
            case FloodReportStatusType.PendingCancel:
                return false;
            default:
                return true;
        }
    };

    const canOrderReport = (floodReport) => {
        if (!canOrder || floodReport?.orderReport) {
            return false;
        }

        if (!floodReport || floodReport?.statusId === null || floodReport?.statusId === undefined) {
            return true;
        }

        switch (floodReport.statusId) {
            case FloodReportStatusType.Complete:
                return floodReport.fileTypeId === FloodReportFileType.Upload;
            case FloodReportStatusType.Cancelled:
            case FloodReportStatusType.Error:
                return true;
            default:
                return false;
        }
    };

    useEffect(() => {
        if (floodReport) {
            if (!floodReport?.orderReport) {
                floodReport.orderReport = false;
            }
            if (!floodReport?.fileTypeId) {
                floodReport.fileTypeId = FloodReportFileType.Upload;
            }
        }
        set_uploadDisabled(!canUploadFile(floodReport));
        set_orderDisabled(!canOrderReport(floodReport));

        const canCancel =
            floodReport?.statusId === FloodReportStatusType.MissingInformation ||
            floodReport?.statusId === FloodReportStatusType.RequiresManualResearch ||
            ((floodReport?.statusId === null || floodReport?.statusId === undefined) && floodReport?.orderReport === true);
        set_cancelDisabled(!canCancel);
    }, [floodReport, canOrder]);

    const onRemoveFloodReportFile = () => {
        if (!floodReport) {
            return;
        }

        floodReport.fileToUpload = null;
        onFloodReportChanged();
    };

    const uploadFloodReport = () => {
        const options = {
            ownerId: null,
            isOpen: true,
            maxFiles: 1,
            fileTypeName: 'Flood Report',
        };
        set_fileSelectOptions(options);
    };

    const onFileSelectSubmit = (id, files) => {
        if (!files || files.length === 0) {
            return;
        }

        floodReport.fileToUpload = files[0];
        onFloodReportChanged();
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const confirmOverwrite = () => {
        set_openConfirmOverwriteModal(true);
    };

    const onOverwriteConfirm = async () => {
        set_openConfirmOverwriteModal(false);
        uploadFloodReport();
    };

    const onOverwriteFileCancel = () => {
        set_openConfirmOverwriteModal(false);
    };

    const onReportCancel = () => {
        if (!floodReport) {
            return;
        }

        if (floodReport.orderReport) {
            set_informModalText('Your Flood Report request was cancelled.');
            set_openInformModal(true);
            floodReport.orderReport = false;
            onFloodReportChanged();
            return;
        }
        onCancel && onCancel();
    };

    const onOrder = () => {
        floodReport.orderReport = true;
        onFloodReportChanged();
        set_informModalText("Your request has been saved and will be placed upon 'Save'");
        set_openInformModal(true);
    };

    const onFloodReportChanged = () => {
        const newFR = { ...floodReport };
        onChange && onChange(newFR);
    };

    return (
        <div style={{ marginBottom: '10px', marginTop: '10px' }}>
            <h2
                className='decorated'
                style={styles.sectionHeader}
            >
                Flood Determination
            </h2>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-2'
                    style={styles.formColumn}
                >
                    <label htmlFor='floodReport'>Flood Report</label>
                </div>
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    <button
                        type='button'
                        className='button hollow'
                        title='Upload new Flood Report'
                        onClick={floodReport?.url ? confirmOverwrite : uploadFloodReport}
                        disabled={uploadDisabled}
                    >
                        Upload
                    </button>
                    &nbsp;
                    <button
                        type='button'
                        className='button'
                        title='Order Flood Report'
                        disabled={orderDisabled}
                        onClick={onOrder}
                    >
                        Order
                    </button>
                    {!canOrder && (
                        <>
                            &nbsp;
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                title='Flood Report can only be ordered if the loan has a BFI Number and a completed address'
                                style={{
                                    position: 'relative',
                                    top: '-6px',
                                    color: '#2c5591',
                                    cursor: 'help',
                                }}
                            />
                        </>
                    )}
                    &nbsp;
                    <button
                        type='button'
                        className='button hollow'
                        title='Cancel Flood Report'
                        onClick={onReportCancel}
                        disabled={cancelDisabled}
                    >
                        Cancel
                    </button>
                </div>
                {floodReport && (
                    <div
                        className='columns small-12 large-7'
                        style={styles.iconContainer}
                    >
                        {floodReport.url && (
                            <Tooltip title='Download file'>
                                <a href={floodReport.url}>
                                    <FontAwesomeIcon
                                        icon={faDownload}
                                        style={floodReport?.url ? styles.iconYes : styles.iconNo}
                                    />
                                </a>
                            </Tooltip>
                        )}
                        &nbsp;
                        {floodReport.fileToUpload && (
                            <span>
                                {floodReport.fileToUpload.name}&nbsp;&nbsp;
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    style={styles.iconCancel}
                                    title={'Cancel Upload: ' + floodReport.fileToUpload.name}
                                    onClick={() => {
                                        onRemoveFloodReportFile();
                                    }}
                                />
                            </span>
                        )}
                    </div>
                )}
            </div>
            {floodReport && floodReport?.statusId >= FloodReportStatusType.PendingRequest && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        &nbsp;
                    </div>
                    <div
                        className='columns small-12 large-10'
                        style={styles.formColumn}
                    >
                        {getStatus(floodReport.statusId)}
                    </div>
                </div>
            )}
            {floodReport && floodReport.statusId === FloodReportStatusType.Error && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        &nbsp;
                    </div>
                    <div
                        className='columns small-12 large-10'
                        style={styles.formColumn}
                    >
                        {floodReport.resultDescription}
                    </div>
                </div>
            )}
            {floodReport && floodReport.statusId === FloodReportStatusType.MissingInformation && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        &nbsp;
                    </div>
                    <div
                        className='columns small-12 large-10'
                        style={styles.formColumn}
                    >
                        <div
                            style={{
                                minWidth: '250px',
                                float: 'left',
                                paddingRight: '20px',
                            }}
                        >
                            <div>
                                <b>Who to Contact</b>
                            </div>
                            <div>{floodReport.contact.name}</div>
                            <div>{floodReport.contact.email}</div>
                            <div>
                                Tel: {formatPhone(floodReport.contact.phone)}
                                {floodReport.contact.extension && <span>ext {floodReport.contact.extension}</span>}
                            </div>
                            {floodReport.contact.fax && <div>Fax: {formatPhone(floodReport.contact.fax)}</div>}
                            <br />
                        </div>
                        <b>What's missing?</b>
                        <div>{floodReport.contact.infoRequired}</div>
                        <div>{floodReport.contact.comments}</div>
                    </div>
                </div>
            )}

            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
            <ConfirmModal
                isOpen={openConfirmOverwriteModal}
                text={'This will overwrite the existing file! Are you sure?'}
                confirmCallback={onOverwriteConfirm}
                cancelCallback={onOverwriteFileCancel}
            />
            <InformModal
                isOpen={openInformModal}
                text={informModalText}
                confirmCallback={() => {
                    set_openInformModal(false);
                }}
            />
        </div>
    );
};
